import React from 'react';
import { Table, Pagination, Form } from 'react-bootstrap';
import { formatDistanceToNow } from 'date-fns';

const PromptLogsTable = ({
  promptLogs,
  currentPage = 1,
  totalPages = 1,
  pageSize = 10,
  sort = "timestamp",
  sortOrder = "desc",
  onPageChange,
  onSortChange,
  onPageSizeChange
}) => {
  // Handle column header click for sorting
  const handleSort = (field) => {
    if (onSortChange) {
      const newSortOrder = sort === field && sortOrder === "asc" ? "desc" : "asc";
      onSortChange(field, newSortOrder);
    }
  };

  // Render sortable column header
  const renderSortableHeader = (label, field) => {
    const isSorted = sort === field;
    const sortIndicator = isSorted ? (sortOrder === "asc" ? " ▲" : " ▼") : "";
    
    return (
      <th 
        onClick={() => handleSort(field)} 
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title={`Click to sort by ${label}`}
      >
        {label}{sortIndicator}
      </th>
    );
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      const date = new Date(timestamp + 'Z'); // Append Z to indicate UTC
      return `${date.toLocaleString()} (${formatDistanceToNow(date, { addSuffix: true })})`;
    } catch (error) {
      return timestamp;
    }
  };

  // Handle page size change
  const handlePageSizeChange = (e) => {
    if (onPageSizeChange) {
      onPageSizeChange(parseInt(e.target.value, 10));
    }
  };

  return (
    <div>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            {renderSortableHeader("Timestamp", "timestamp")}
            {renderSortableHeader("Module", "module")}
            {renderSortableHeader("Function", "function")}
            {renderSortableHeader("Model", "model")}
            {renderSortableHeader("Case ID", "case_id")}
            {renderSortableHeader("Execution ID", "nextlog_execution_id")}
            {renderSortableHeader("Prompt Tokens", "prompt_tokens")}
            {renderSortableHeader("Completion Tokens", "completion_tokens")}
            {renderSortableHeader("Total Tokens", "total_tokens")}
            <th>Temperature</th>
            <th>Max Tokens</th>
            <th>Num Chars</th>
          </tr>
        </thead>
        <tbody>
          {promptLogs.length === 0 ? (
            <tr>
              <td colSpan="12" className="text-center">No prompt logs found</td>
            </tr>
          ) : (
            promptLogs.map((log) => (
              <tr key={log.prompt_log_id}>
                <td style={{ whiteSpace: 'nowrap' }}>{formatTimestamp(log.timestamp)}</td>
                <td>{log.module || "N/A"}</td>
                <td>{log.function || "N/A"}</td>
                <td>{log.model || "N/A"}</td>
                <td>{log.case_id !== null && log.case_id !== undefined ? log.case_id : "N/A"}</td>
                <td>{log.nextlog_execution_id !== null && log.nextlog_execution_id !== undefined ? log.nextlog_execution_id : "N/A"}</td>
                <td>{log.prompt_tokens !== null && log.prompt_tokens !== undefined ? log.prompt_tokens.toLocaleString() : "N/A"}</td>
                <td>{log.completion_tokens !== null && log.completion_tokens !== undefined ? log.completion_tokens.toLocaleString() : "N/A"}</td>
                <td>{log.total_tokens !== null && log.total_tokens !== undefined ? log.total_tokens.toLocaleString() : "N/A"}</td>
                <td>{log.temperature !== null && log.temperature !== undefined ? log.temperature : "N/A"}</td>
                <td>{log.max_tokens !== null && log.max_tokens !== undefined ? log.max_tokens.toLocaleString() : "N/A"}</td>
                <td>{log.num_chars !== null && log.num_chars !== undefined ? log.num_chars.toLocaleString() : "N/A"}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Pagination and Page Size Controls */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center">
          <Form.Label className="mb-0 me-2">Rows per page:</Form.Label>
          <Form.Select 
            size="sm" 
            value={pageSize} 
            onChange={handlePageSizeChange}
            style={{ width: 'auto' }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Form.Select>
        </div>

        <small className="text-muted">
          Page {currentPage} of {totalPages}
        </small>

        {totalPages > 1 && (
          <Pagination size="sm" className="mb-0">
            <Pagination.First
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            
            {/* Show current page and adjacent pages */}
            {currentPage > 2 && <Pagination.Ellipsis disabled />}
            {currentPage > 1 && (
              <Pagination.Item onClick={() => onPageChange(currentPage - 1)}>
                {currentPage - 1}
              </Pagination.Item>
            )}
            <Pagination.Item active>{currentPage}</Pagination.Item>
            {currentPage < totalPages && (
              <Pagination.Item onClick={() => onPageChange(currentPage + 1)}>
                {currentPage + 1}
              </Pagination.Item>
            )}
            {currentPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
            
            <Pagination.Next
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        )}
      </div>
    </div>
  );
};

export default PromptLogsTable;
